import { ReactElement, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { styled } from '@zoopla-eng/zds-components/themes';
import { AdSideBarLayout } from '@zoopla-eng/zds-components/page';
import type { BreadcrumbsProps } from '@zoopla-eng/zds-components/breadcrumbs';
import { Breadcrumbs } from '@zoopla-eng/zds-components/breadcrumbs';
import { Box } from '@zoopla-eng/zds-components/box';
import { Stack } from '@zoopla-eng/zds-components/stack';
import { ColouredCard } from '@zoopla-eng/zds-components/coloured-card';
import { CardTitle } from '@zoopla-eng/zds-components/headings';
import { Link } from '@zoopla-eng/zds-components/link';
import { Footer } from '@zoopla/footer';
import {
    AD_UNIT_ROOTS,
    AD_UNIT_SECTION,
    AdvertProvider,
    AdvertSlotDfp,
    ADBRIDG_CLASS,
    DfpTargeting
} from '@zoopla/adverts';
import Header from 'shared/components/Header';
import ConsentBanner from 'shared/components/ConsentBanner';
import AdScripts from 'shared/components/AdScripts';

const GoogleOneTap = dynamic<Record<string, unknown>>(
    () =>
        import('@zoopla/authentication/modal').then(
            (module) => module.GoogleOneTap
        ),
    {
        ssr: false
    }
);

const MainScript = dynamic(() => import('shared/components/main'), {
    ssr: false
});

const Sticky = styled('div', {
    position: 'sticky',
    top: 0,
    width: '100%'
});

const Adverts = () => (
    <>
        <AdvertSlotDfp
            position={0}
            showOnly={['>', 960]}
            sizes={[[300, 16], 'fluid']}
            adUnitCode={[
                AD_UNIT_ROOTS.TEXT_LINK,
                AD_UNIT_SECTION.HOUSE_PRICES,
                '/House-Prices'
            ]}
            adbridgClass={ADBRIDG_CLASS.TEXT_LINK}
        />
        <AdvertSlotDfp
            position={1}
            showOnly={['>', 960]}
            sizes={[[300, 250]]}
            adUnitCode={[
                AD_UNIT_ROOTS.MPU,
                AD_UNIT_SECTION.HOUSE_PRICES,
                '/House-Prices'
            ]}
            adbridgClass={ADBRIDG_CLASS.MPU}
        />
        <Sticky>
            <AdvertSlotDfp
                position={2}
                showOnly={['>', 960]}
                sizes={[[300, 600]]}
                adUnitCode={[
                    AD_UNIT_ROOTS.SKY_SCRAPER,
                    AD_UNIT_SECTION.HOUSE_PRICES,
                    '/House-Prices'
                ]}
                adbridgClass={ADBRIDG_CLASS.SKYSCRAPER}
            />
        </Sticky>
    </>
);

const stackProps = {
    rowGap: '$S400',
    '@L100': {
        flexFlow: 'row'
    }
};

const BottomRail = () => (
    <Box py="$M300" bpL100={{ py: '$L200' }}>
        <Stack {...stackProps}>
            <ColouredCard backgroundColor="$graphicPriceLight">
                <Box p="$S500">
                    <Box pb="$S400">
                        <CardTitle>How much is my home worth?</CardTitle>
                    </Box>
                    <Link>Check price estimate</Link>
                </Box>
            </ColouredCard>
            <ColouredCard backgroundColor="$graphicBackgroundAccentDark">
                <Box p="$S500">
                    <Box pb="$S400">
                        <CardTitle>Get closer to what matters</CardTitle>
                    </Box>
                    <Link>Search homes by travel time</Link>
                </Box>
            </ColouredCard>
            <ColouredCard backgroundColor="$graphicImprovingLight">
                <Box p="$S500">
                    <Box pb="$S400">
                        <CardTitle>Find an agent</CardTitle>
                    </Box>
                    <Link>Look for local agents</Link>
                </Box>
            </ColouredCard>
        </Stack>
    </Box>
);

const adBridgPage = AD_UNIT_SECTION.HOUSE_PRICES;
const targetingDefaults = {
    activity: 'house_prices_landing',
    page: '/house-prices/browse/*'
};

/**
 * Default layout used across site, includes web chrome i.e header, footer etc
 */
export const Layout = ({
    breadcrumbs,
    dfpAdTargeting,
    pageNumber,
    children
}: {
    breadcrumbs?: BreadcrumbsProps['items'];
    dfpAdTargeting: DfpTargeting;
    pageNumber?: number;
    children: ReactElement;
}) => {
    const [advertProviderContext, updateAdvertProviderContext] = useState({
        targeting: {
            ...targetingDefaults,
            ...dfpAdTargeting
        },
        pageNumber,
        adBridgPage
    });

    useEffect(() => {
        updateAdvertProviderContext({
            targeting: {
                ...targetingDefaults,
                ...dfpAdTargeting
            },
            pageNumber,
            adBridgPage
        });
    }, [dfpAdTargeting, pageNumber]);

    return (
        <>
            <AdScripts />
            <ConsentBanner />
            <MainScript />
            <AdSideBarLayout
                adverts={
                    <AdvertProvider value={advertProviderContext}>
                        <Adverts />
                    </AdvertProvider>
                }
                bottomRail={<BottomRail />}
                header={<Header noShadow />}
                footer={<Footer hideCards />}
                main={
                    <>
                        {breadcrumbs && (
                            <Box
                                fw
                                py="$S200"
                                pl="$S400"
                                bpS200={{ pl: 0 }}
                                display="none"
                                bpM100={{ display: 'block' }}
                            >
                                <Breadcrumbs items={breadcrumbs} />
                            </Box>
                        )}
                        {children}
                        {breadcrumbs && (
                            <Box
                                fw
                                pl="$S400"
                                pt="$L100"
                                bpS200={{ pl: 0 }}
                                bpM100={{ display: 'none' }}
                            >
                                <Breadcrumbs items={breadcrumbs} />
                            </Box>
                        )}
                    </>
                }
            />
            <GoogleOneTap />
        </>
    );
};
